import React, { useState } from 'react';
import { Card, Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { calculateWorkingDaysAPI } from '../../services/api';

const WorkingDaysCalculator = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Calculate working days
    const calculateDays = async () => {
        if (!startDate || !endDate) {
            setError('Başlangıç ve bitiş tarihi seçiniz');
            return;
        }

        if (new Date(endDate) < new Date(startDate)) {
            setError('Bitiş tarihi başlangıç tarihinden sonra olmalıdır');
            return;
        }

        try {
            setLoading(true);
            setError('');
            setResult(null);

            const response = await calculateWorkingDaysAPI(startDate, endDate);

            if (response.success) {
                setResult(response.data);
            }
        } catch (error) {
            console.error('Calculate working days error:', error);
            setError(error.response?.data?.message || 'Hesaplama sırasında hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setStartDate('');
        setEndDate('');
        setResult(null);
        setError('');
    };

    // Format Turkish date
    const formatTurkishDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <Card>
            <Card.Header>
                <h5 className="mb-0">💼 İş Günü Hesaplayıcısı</h5>
            </Card.Header>

            <Card.Body>
                {/* Error Message */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Date Input Form */}
                <Row className="mb-3">
                    <Col md={4}>
                        <Form.Label>Başlangıç Tarihi</Form.Label>
                        <Form.Control
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </Col>
                    <Col md={4}>
                        <Form.Label>Bitiş Tarihi</Form.Label>
                        <Form.Control
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                        />
                    </Col>
                    <Col md={4} className="d-flex align-items-end">
                        <Button
                            variant="primary"
                            onClick={calculateDays}
                            disabled={loading || !startDate || !endDate}
                            className="me-2"
                        >
                            {loading ? 'Hesaplanıyor...' : 'Hesapla'}
                        </Button>
                        <Button
                            variant="outline-secondary"
                            onClick={resetForm}
                            disabled={loading}
                        >
                            Temizle
                        </Button>
                    </Col>
                </Row>

                {/* Results */}
                {result && (
                    <Alert variant="info" className="mb-0">
                        <Row>
                            <Col md={12}>
                                <h6 className="mb-3">📊 Hesaplama Sonuçları</h6>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={4}>
                                <div className="text-center p-3 bg-light rounded">
                                    <h4 className="text-primary mb-1">{result.totalDays}</h4>
                                    <small className="text-muted">Toplam Gün</small>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                                    <h4 className="text-success mb-1">{result.workingDays}</h4>
                                    <small className="text-muted">İş Günü</small>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
                                    <h4 className="text-warning mb-1">{result.excludedDays}</h4>
                                    <small className="text-muted">Hariç Tutulan</small>
                                </div>
                            </Col>
                        </Row>

                        {/* Breakdown */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <strong>Tarih Aralığı:</strong><br />
                                <small className="text-muted">
                                    {formatTurkishDate(result.startDate)} - {formatTurkishDate(result.endDate)}
                                </small>
                            </Col>
                            <Col md={6}>
                                <strong>Detaylar:</strong><br />
                                <Badge bg="secondary" className="me-1">
                                    🏖️ Hafta sonu: {result.breakdown?.weekends || 0}
                                </Badge>
                                <Badge bg="info">
                                    🎉 Tatil: {result.breakdown?.holidays || 0}
                                </Badge>
                            </Col>
                        </Row>

                        {/* Holiday Names */}
                        {result.breakdown?.holidayNames?.length > 0 && (
                            <Row>
                                <Col md={12}>
                                    <strong>Araya Giren Tatiller:</strong><br />
                                    <small className="text-success">
                                        {result.breakdown.holidayNames.join(', ')}
                                    </small>
                                </Col>
                            </Row>
                        )}

                        {/* Info Note */}
                        <hr className="my-3" />
                        <small className="text-muted">
                            ℹ️ Bu hesaplamada hafta sonları ve resmi tatiller iş günü olarak sayılmamaktadır.
                            Hafta sonu + tatil örtüştüğünde tek değerlendirme yapılır.
                        </small>
                    </Alert>
                )}
            </Card.Body>
        </Card>
    );
};

export default WorkingDaysCalculator;